import BaseObjectDirective from './BaseObjectDirective';

/**
 * AutoPlotFromTableController is a directive that handles the automatic plotting of charts from table data.
 * It supports pie, bar, and scatter charts.
 */
class AutoPlotFromTableController extends BaseObjectDirective {
    /**
     * Constructor for AutoPlotFromTableController.
     * @param {Object} $scope - Angular scope.
     * @param {Object} $rootScope - Angular root scope.
     * @param {Object} $injector - Angular injector service.
     * @param {Object} $uibModal - Angular UI Bootstrap modal service.
     * @param {Object} $routeParams - Angular route parameters.
     * @param {Object} TaskDataService - Service for task data.
     * @param {Object} $signals - Signal service.
     * @param {Object} ObjectInstanceRegistrationService - Service for object instance registration.
     * @param {Object} $timeout - Angular timeout service.
     * @param {Object} $bridges - Bridge service.
     * @param {Object} $translate - Angular translate service.
     * @param {Object} Sanitize - Sanitization service.
     */
    constructor($scope, $rootScope, $injector, $uibModal, $routeParams, TaskDataService, $signals, ObjectInstanceRegistrationService, $timeout, $bridges, $translate, Sanitize) {
        super($injector, $scope);
        this._logIdentifier = 'AutoPlotFromTableDirective';
        this.applyChartChangesTimeout = false;
        this.$logger = $bridges.logger();
        this._sanitize = Sanitize();
        this._sanitize.disableTags();

        /**
         * Applies chart changes based on the selected chart type and data.
         */
        this.applyChartChanges = function() {
            if (angular.isDefined($scope.chartSettings)) {
                if ($scope.chartSettings.selectedChart == 'pie' && angular.isDefined($scope.chartSettings.pie.values) && $scope.chartSettings.pie.values.length > 0) {
                    $scope.plotGraph();
                }

                if ($scope.chartSettings.selectedChart == 'bar' && angular.isDefined($scope.chartSettings.x) && $scope.chartSettings.x.length > 0) {
                    let doit = true;

                    angular.forEach($scope.series, function(s) {
                        if (!angular.isDefined(s.y) || s.y.length == 0) {
                            doit = false;
                        }
                    });

                    if (doit) {
                        $scope.plotGraph();
                    }
                }

                if ($scope.chartSettings.selectedChart == 'scatter') {
                    for (let i = 0; i < $scope.series.length; i++){
                        let s = $scope.series[i];
                        if (s.xVal > 0){
                            s.sortedX = [];
                            if (s.xType == "col"){
                                for (let j = 0; j < s.data.length; j++){
                                    s.sortedX.push(s.data[j][s.xVal-1]);
                                }
                            } else {
                                for (let j = 0; j < s.data[s.xVal-1].length; j++){
                                    s.sortedX.push(s.data[s.xVal-1][j]);
                                }
                            }
                        }
                        if (s.yVal > 0){
                            s.sortedY = [];
                            if (s.yType == "col"){
                                for (let j = 0; j < s.data.length; j++){
                                    s.sortedY.push(s.data[j][s.yVal-1]);
                                }
                            } else {
                                for (let j = 0; j < s.data[s.yVal-1].length; j++){
                                    s.sortedY.push(s.data[s.yVal-1][j]);
                                }
                            }
                        }
                    }
                    let doit = true;

                    angular.forEach($scope.series, function(s) {
                        if (!angular.isDefined(s.y) || s.y.length == 0 || !angular.isDefined(s.x) || s.x.length == 0) {
                            doit = false;
                        }
                    });

                    if (doit) {
                        $scope.plotGraph();
                    }
                }
            }
        };

        /**
         * Watches for changes in the series and applies chart changes with a timeout.
         */
        $scope.$watch('series', function(newVal, oldVal) {
            if (this.applyChartChangesTimeout && angular.isFunction(this.applyChartChangesTimeout.then)) {
                $timeout.cancel(this.applyChartChangesTimeout);
            }
            this.applyChartChangesTimeout = $timeout(this.applyChartChanges, 600);
        }.bind(this), true);

        /**
         * Watches for changes in chart settings and applies chart changes with a timeout.
         * @param {Object} newVal - New value of the watched property.
         * @param {Object} oldVal - Old value of the watched property.
         */
        let watchChartSettings = function(newVal, oldVal) {
            if (this.applyChartChangesTimeout && angular.isFunction(this.applyChartChangesTimeout.then)) {
                $timeout.cancel(this.applyChartChangesTimeout);
            }
            this.applyChartChangesTimeout = $timeout(this.applyChartChanges, 600);
        }.bind(this);

        $scope.$watch('chartSettings.title', watchChartSettings, true);
        $scope.$watch('chartSettings.pie', watchChartSettings, true);
        $scope.$watch('chartSettings.bar', watchChartSettings, true);

        /**
         * Configures the table settings and handles cell selection.
         */
        $scope.tableConfig = {
            rowHeaders: true,
            colHeaders: true,
            readOnly: true,
            stretchH: 'all',
            afterSelectionEnd: function(rs, cs, re, ce) {
                if ($scope.selectionMode == 'fieldSelection') {
                    let valid = true;
                    if (rs == re) {
                        let type = 'row';
                    } else if (cs == ce) {
                        let type = 'col';
                    } else {
                        valid = false;
                        $scope.table.deselectCell();
                    }

                    if (valid) {
                        let data = $scope.table.getData(rs, cs, re, ce);
                        let newData = [];

                        angular.forEach(data, function(outer) {
                            angular.forEach(outer, function(inner) {
                                if (angular.isDefined(inner) && inner !== null) {
                                    inner = inner.replace(',', '.');
                                    newData.push(inner);
                                }
                            });
                        });

                        data = newData;

                        $rootScope.$safeApply(function() {
                            if ($scope.selectionAxis == 'x') {
                                angular.forEach($scope.series, function(v, k) {
                                    $scope.series[k][$scope.selectionAxis] = data;
                                    $scope.series[k][$scope.selectionAxis + 'Display'] = data.join();
                                });

                                $scope.chartSettings.x = data;
                                $scope.chartSettings.xDisplay = data.join();
                            } else if ($scope.selectionKey == 'pie') {
                                if (typeof($scope.chartSettings['pie']) == 'undefined') {
                                    $scope.chartSettings['pie'] = [];
                                }
                                $scope.chartSettings['pie'][$scope.selectionAxis] = data;
                            } else {
                                $scope.series[$scope.selectionKey]['y'] = data;
                                $scope.series[$scope.selectionKey]['yDisplay'] = data.join();
                            }
                            $scope.selectionMode = "Done";
                            $timeout(function() {
                                $scope.selectionMode = false;
                            }, 300)
                        });
                    }
                }
            },

            afterOnCellMouseDown: function(event, coords, td) {
                if ((coords.col < 0 || coords.row < 0) && $scope.selectionMode == 'Full') {
                    if (coords.col < 0) {
                        let selectedRowCol = coords.row;
                        let type = 'row';

                        if ($scope.selectionAxis == 'x') {
                            $scope.sortDataByX('row', parseInt(coords.row), $scope.selectionKey);
                        }
                        let selectionValues = $scope.table.getDataAtRow(coords.row);
                    } else {
                        let selectedRowCol = coords.col;
                        let type = 'col';
                        if ($scope.selectionAxis == 'x') {
                            $scope.sortDataByX('col', parseInt(coords.col), $scope.selectionKey);
                        }
                        let selectionValues = $scope.table.getDataAtCol(coords.col);
                    }

                    let newData = [];
                    angular.forEach(selectionValues, function(outer) {
                        if(angular.isDefined(outer) && typeof(outer) != 'object'){
                            outer = outer.replace(',', '.');
                            newData.push(outer);
                        }
                    });

                    selectionValues = newData;

                    if ($scope.selectionKey == 'all' && $scope.selectionAxis == 'x') {
                        angular.forEach($scope.series, function(v, k) {
                            $scope.series[k][$scope.selectionAxis] = selectionValues;
                        });
                        $scope.chartSettings.x = selectionValues;
                    } else {
                        $scope.series[$scope.selectionKey][$scope.selectionAxis] = selectionValues;
                    }

                    $rootScope.$safeApply(function() {
                        if ($scope.selectionKey != 'all') {
                            $scope.series[$scope.selectionKey][$scope.selectionAxis + 'Val'] = selectedRowCol + 1;
                            $scope.series[$scope.selectionKey][$scope.selectionAxis + 'Type'] = type;
                            $scope.series[$scope.selectionKey][$scope.selectionAxis + 'Display'] = selectionValues;
                        }
                        $scope.selectionMode = "Done";
                        $timeout(function() {
                            $scope.selectionMode = false;
                        }, 500)
                    });
                }
            }
        };

        let colorArray = ['#FF0000', '#0000FF', '#FFFF00', '#00FFFF'];
        $scope.colorFunction = function() {
            return function(d, i) {
                return colorArray[i];
            };
        };

        $scope.showBarChart = false;
        $scope.selectionMode = false;
        $scope.selectionKey = '';
        $scope.selectionAxis = '';
        $scope.series = [];
        $scope.showCharts = false;
        $scope.isCMS = $rootScope.isCMS;
        $scope.taskuuid = $routeParams.taskUUID;

        $scope.chartSettings = {
            'selectedChart': 'pie',
            'pie': {},
            'scatterLine' : {}
        };

        $scope.showWizard = false;
        $scope.maxSeries = 3;

        $scope.availableCharts = [{
            'name': $translate.instant('AUTOPLOT.SCATTER'),
            'internal': 'scatter',
            'disabled': false
        }, {
            'name': $translate.instant('AUTOPLOT.PIE'),
            'internal': 'pie',
            'disabled': false
        }, {
            'name': $translate.instant('AUTOPLOT.BAR'),
            'internal': 'bar',
            'disabled': false
        }];

        $scope.iFrameStyle = {
            'width': '850px',
            'height': '900px'
        };

        $scope.scatterGraphIframeUUID = $scope.contentObject.getUUID() + '_scatter';
        $scope.scatterGraphURL = "/iframe-tools/scatter-graph/index.html?uuid=" + $scope.scatterGraphIframeUUID

        $scope.scatterGraphObject = false;
        let plotAfterIframeReady = false;
        $signals.on('iFrameReady', function(objectUUID) {
            if (typeof objectUUID != 'undefined' && objectUUID == $scope.scatterGraphIframeUUID) {
                $scope.scatterGraphObject = ObjectInstanceRegistrationService.getObjectByUUID(objectUUID);
                $scope.chartSettings.title = "Title";
                if (plotAfterIframeReady) {
                    $scope.plotGraph();
                }
            }
        });

        /**
         * Plots the graph based on the selected chart type.
         */
        $scope.plotGraph = function() {
            $scope.loading = true;
            $timeout(function() {
                if ($scope.chartSettings.selectedChart == 'scatter') {
                    $scope.plotScatterGraph();
                }

                if ($scope.chartSettings.selectedChart == 'bar') {
                    $scope.plotBarGraph();
                }

                if ($scope.chartSettings.selectedChart == 'pie') {
                    $scope.plotPieChart();
                }

                $scope.showCharts = true;
                $scope.loading = false;
            }, 0);
        };

        /**
         * Plots a pie chart.
         */
        $scope.plotPieChart = function() {
            let vals = [];
            $scope.chartSettings.pieData = [];
            angular.forEach($scope.chartSettings['pie']['labels'], function(v, k) {
                let val = typeof($scope.chartSettings['pie']['values'][k]) != 'undefined' ? $scope.chartSettings['pie']['values'][k] : 0;
                let obj = {
                    'label': v,
                    'value': val
                };
                vals.push(obj);
            });

            $scope.loadPie = true;
            $scope.chartSettings.pieData = vals;
        };

        /**
         * Plots a bar graph.
         */
        $scope.plotBarGraph = function() {
            let newBarData = [];
            angular.forEach($scope.series, function(s, key) {
                let values = [];
                for (let i = 0; i < s.x.length; i++) {
                    if (typeof(s.y[i]) != 'undefined') {
                        let vals = {
                            'x': s.x[i],
                            'y': parseFloat(s.y[i].replace(',', ','))
                        };
                        values.push(vals);
                    }
                }

                let data = {
                    'key': s.name,
                    'values': values
                };
                newBarData.push(data);
            });
            console.log('_apft','barGraphData Rounded? 1', newBarData);
            $scope.chartSettings.barData = newBarData;
            $scope.showBarChart = true;
        };

        /**
         * Plots a scatter graph.
         */
        $scope.plotScatterGraph = function() {
            let seriesCount = 0;

            let shapes = ['circle', 'triangle-up', 'triangle-down', 'diamond', 'square'];
            let scatterData = [];

            for (let i = 0; i < $scope.series.length; i++){
                let s = $scope.series[i];
                $scope.table.loadData(s.data);
                $scope.table.render();
                $scope.series.active = true;

                let xData = (s.xType == 'col') ? $scope.table.getDataAtCol(s.xVal - 1) : $scope.table.getDataAtRow(s.xVal - 1),
                    yData = (s.yType == 'col') ? $scope.table.getDataAtCol(s.yVal - 1) : $scope.table.getDataAtRow(s.yVal - 1);

                scatterData.push({
                    key: s.name,
                    values: []
                });

                for (let j = 0; j < xData.length; j++) {
                    if(angular.isDefined(xData[j]) && xData[j] !== null && angular.isDefined(yData[j]) && yData[j] !== null){
                        scatterData[i].values.push({
                            x: parseFloat(xData[j].replace(',', '.')),
                            y: parseFloat(yData[j].replace(',', '.')),
                            shape: shapes[i]
                        });
                    }
                }
            }
            $scope.chartSettings.scatterData = scatterData;
        };

        /**
         * Sorts data based on the X-axis values.
         * @param {string} type - The type of sorting (row or column).
         * @param {number} index - The index to sort by.
         * @param {string} seriesKey - The key of the series to sort.
         */
        $scope.sortDataByX = function(type, index, seriesKey) {
            let data = seriesKey == 'all' ? $scope.table.getData() : $scope.series[seriesKey].data;

            if (type == "row") {
                let newData = [];
                angular.forEach(data, function(val, key) {
                    angular.forEach(val, function(v, k) {
                        if (typeof newData[k] == 'undefined') {
                            newData[k] = [];
                        }
                        newData[k].push(v);
                    });
                });
                data = newData;
            }

            let i = 0;
            let arr = [];
            let keyArr = [];
            let sortedArray = [];

            angular.forEach(data, function(val, key) {
                let xVal = val[index];
                let keyString = 0o0 + xVal + '_' + i;
                arr[keyString] = val;
                keyArr.push(keyString);
                i++;
            });

            keyArr.sort(function(a,b){
                a = a.split('_')[0];
                b = b.split('_')[0];
                return a-b;
            });

            angular.forEach(keyArr, function(val, key) {
                sortedArray.push(arr[val]);
            });

            if (type == "row") {
                let clean = []

                for (let i = 0; i < sortedArray[0].length; i++) {
                    clean[i] = [];
                    angular.forEach(sortedArray, function(d) {
                        clean[i].push(d[i]);
                    });
                }

                sortedArray = clean;
            }

            if (seriesKey == 'all') {
                angular.forEach($scope.series, function(v, k) {
                    $scope.series[k].data = sortedArray;
                });
            } else {
                $scope.series[seriesKey].data = sortedArray;
            }

            $scope.table.loadData(sortedArray);
            $scope.table.render();
        };

        /**
         * Enables selection mode for chart settings.
         * @param {string} key - The key for selection.
         * @param {string} axis - The axis for selection.
         * @param {string} mode - The mode for selection.
         */
        $scope.enableSelection = function(key, axis, mode) {
            $scope.selectionMode = mode;
            $scope.selectionKey = key;
            $scope.selectionAxis = axis;
        };

        /**
         * Disables selection mode.
         */
        $scope.disableSelectionMode = function() {
            $scope.selectionMode = false;
        };

        /**
         * Changes the active tab in the series.
         * @param {string} key - The key of the series to activate.
         */
        $scope.changeTab = function(key) {
            angular.forEach($scope.series, function(v, k) {
                if (k != key) {
                    v.active = false;
                } else {
                    v.active = true;
                }
            });

            $scope.table.loadData($scope.series[key].data);
            $scope.table.render();
        };

        /**
         * Initializes table data based on step data and alignment.
         * @param {Array} stepData - The data from the steps.
         * @param {string} alignment - The alignment of the data (vertical or horizontal).
         * @param {Object} stepTwo - The second step configuration.
         */
        $scope.initTableData = function(stepData, alignment, stepTwo) {
            let tableData = [];

            angular.forEach(stepData, function(val, key) {
                if (typeof(tableData[val.row]) !== 'object') {
                    tableData[val.row] = [];
                }
                let data;
                if (val.data) {
                    data = this._sanitize.sanitizeHtml(val.data);
                }
                tableData[val.row][String(val.col)] = data;
            });

            let header = [];

            if (alignment == 'vertical') {
                angular.forEach(tableData, function(row, k) {
                    header[k] = row[0];
                    row.splice(0, 1);
                });

                if(angular.isDefined(header) && header.length > 0){
                    $scope.tableConfig['rowHeaders'] = header;
                }else{
                    $scope.tableConfig['rowHeaders'] = true;
                }
                $scope.tableConfig['colHeaders'] = true;
            } else {
                header = tableData[0];
                tableData.splice(0, 1);
                if(angular.isDefined(header) && header.length > 0){
                    $scope.tableConfig['colHeaders'] = header;
                }else{
                    $scope.tableConfig['colHeaders'] = true;
                }
                $scope.tableConfig['rowHeaders'] = true;
            }

            if(tableData.length === 0){
                $scope.newTableData = [
                    ['','']
                ];
            }else{
                $scope.newTableData = tableData;
            }

            let arrayLength = angular.copy($scope.newTableData.length);

            for(let i = 0; i < arrayLength; i++){
                if(typeof($scope.newTableData[i]) == 'undefined'){
                    $scope.newTableData[i] = [];
                }
            }

            try {
                $scope.table.loadData($scope.newTableData);
            } catch (e) {
                console.error("Can't set table data", $scope.newTableData, e);
            }

            if($scope.newTableData.length >= 1){
                $scope.tableConfig['maxRows'] = $scope.newTableData.length;
                $scope.tableConfig['maxCols'] = stepTwo.cols;
                $scope.tableConfig['minRows'] = $scope.newTableData.length;
                $scope.tableConfig['minCols'] = stepTwo.cols;
            }else{
                $scope.tableConfig['maxRows'] = stepTwo.rows;
                $scope.tableConfig['maxCols'] = stepTwo.cols;
                $scope.tableConfig['minRows'] = stepTwo.rows;
                $scope.tableConfig['minCols'] = stepTwo.cols;
            }

            try {
                $scope.table.updateSettings($scope.tableConfig);
            } catch (e) {
                console.error("Can't update table settings",e, $scope.tableConfig);
            }

            $scope.table.render();
        };

        /**
         * Deletes a series from the chart settings.
         * @param {number} key - The key of the series to delete.
         * @param {Event} evnt - The event object.
         */
        $scope.deleteSeries = function(key, evnt) {
            evnt.preventDefault();
            evnt.stopPropagation();
            $scope.disableSelectionMode();
            $scope.series.splice(key, 1);

            if (angular.isDefined($scope.series[key - 1])) {
                $scope.series[key - 1].active = true;
            } else if (angular.isDefined($scope.series[0])) {
                $scope.series[0].active = true;
            }
        };

        /**
         * Adds a new series to the chart settings.
         */
        $scope.addNewSeries = function() {
            let count = $scope.series.length + 1;

            if ($scope.chartSettings.selectedChart == 'scatter') {
                let data = $scope.newTableData;
                let xVal = '';
                let xDisplay = '';
            } else {
                let data = $scope.table.getData();
                let xVal = $scope.chartSettings.x;
                let xDisplay = $scope.chartSettings.xDisplay;
            }

            let array = {
                'name': 'Series' + count,
                'active': true,
                'data': data,
                'x': xVal,
                'xDisplay': xDisplay
            };
            $scope.series.push(array);
            $scope.changeTab($scope.series.length - 1);
        };

        /**
         * Selects a chart type and resets the chart settings.
         * @param {string} value - The type of chart to select.
         */
        $scope.selectChart = function(value) {
            $scope.series = [];
            $scope.showWizard = true;

            $scope.selectionMode = false;

            $scope.chartSettings.title = "";
            $scope.chartSettings.xLabel = "";
            $scope.chartSettings.yLabel = "";
            $scope.chartSettings.x = "";
            $scope.chartSettings.xDisplay = "";
            $scope.chartSettings.xValues = "";
            $scope.chartSettings.y = "";
            $scope.chartSettings.pie = {};
            $scope.chartSettings.pieData = [];
            $scope.chartSettings.barData = [];
            $scope.chartSettings.scatterData = [];
            $scope.chartSettings.scatterLine = {};

            $scope.chartSettings.selectedChart = value;
        };

        /**
         * Synchronizes the table with the current data.
         */
        let syncTable = function() {
            if (angular.isDefined($scope.tableController.getStepData()[3]) && angular.isDefined($scope.tableController.getStepData()[1].alignment && angular.isDefined($scope.tableController.getStepData()[2]))) {
                $scope.initTableData($scope.tableController.getStepData()[3]['tableData'], $scope.tableController.getStepData()[1].alignment, $scope.tableController.getStepData()[2]);
            } else {
                console.log("Information needed not provided");
            }
        };

        $timeout(function() {
            let targetTableObjectUUID = $scope.contentObject.getPreferences().targetTableObject;
            if (!angular.isDefined(targetTableObjectUUID) || targetTableObjectUUID === '') {
                return;
            }

            let targetTableObject = ObjectInstanceRegistrationService.getObjectByUUID(targetTableObjectUUID);
            if (angular.isDefined(targetTableObject)) {
                $scope.tableController = targetTableObject;
                syncTable();

                let changeListener = function() {
                    syncTable();
                    $scope.resetWizard();
                };

                targetTableObject.registerChangeListener(changeListener);
            } else {
                console.error("AutoPlot Target TableObject with UUID " + targetTableObjectUUID + " not found!");
            }
        });

        /**
         * Resets the wizard and chart settings.
         */
        $scope.resetWizard = function() {
            $rootScope.$safeApply(function() {
                $scope.chartSettings.pie = {};
                $scope.chartSettings.bar = {};
                $scope.chartSettings.barData = [];
                $scope.chartSettings.scatterData = [];
                $scope.chartSettings.pieData = [];
                $scope.chartSettings.xDisplay = '';
                $scope.chartSettings.yDisplay = '';
                $scope.series = [];
                $scope.showCharts = false;
                $scope.selectionMode = false;
            });
        };

        /**
         * Initializes preferences for the chart settings.
         */
        $scope.initPreferences = function() {
            let targetTableObject = (typeof $scope.contentObject.getPreferences() != "undefined" ? $scope.contentObject.getPreferences().targetTableObject : undefined),
                canDrawLine = (typeof $scope.contentObject.getPreferences() != "undefined" ? $scope.contentObject.getPreferences().canDrawLine : false),
                canShowAutoLine = (typeof $scope.contentObject.getPreferences() != "undefined" ? $scope.contentObject.getPreferences().canShowAutoLine : false),
                canShowTooltip = (typeof $scope.contentObject.getPreferences() != "undefined" ? $scope.contentObject.getPreferences().canShowTooltip : false);

            $scope.disabledCheckbox = {
                'canDrawLine' : canShowAutoLine,
                'canShowAutoLine' : canDrawLine
            };

            $scope.$watch('preferencesValues', function(val1, val2) {
                if (val1 == val2)
                    return;
                let preferences = {};
                angular.forEach($scope.preferencesValues, function(value, key) {
                    preferences[key] = value;
                });

                $timeout(function () {
                    $scope.$apply(function(){
                        $scope.disabledCheckbox['canDrawLine'] = $scope.preferencesValues['canShowAutoLine'];
                        $scope.disabledCheckbox['canShowAutoLine'] = $scope.preferencesValues['canDrawLine'];
                    });
                }, 1);

                $scope.contentObject.setPreferences(preferences);
                if (val1.targetTableObject != val2.targetTableObject){
                    let targetTableObject = ObjectInstanceRegistrationService.getObjectByUUID(val1.targetTableObject);
                    if (angular.isDefined(targetTableObject)) {
                        $scope.tableController = targetTableObject;
                        syncTable();
                        $scope.resetWizard();
                    }
                }
            }, true);

            $scope.preferencesValues = {};
            $scope.preferencesValues['targetTableObject'] = targetTableObject;
            $scope.preferencesValues['canShowTooltip'] = canShowTooltip;
            $scope.preferencesValues['canDrawLine'] = canDrawLine;
            $scope.preferencesValues['canShowAutoLine'] = canShowAutoLine;
            $scope.preferencesValues['includedInPDF'] = typeof includedInPDF !== 'undefined' ? includedInPDF : true;

            $scope.preferences = [
                {
                    '01-targetTableObject': [{
                        'type': 'select-other-content-object',
                        'name': 'Source Table',
                        'valueKey': 'targetTableObject',
                        'objectType': 'table'
                    }],
                    'includedInPDF': [{
                        'type': 'checkbox',
                        'name': 'Included in Response PDF',
                        'valueKey': 'includedInPDF'
                    }],
                    '02-lobf': [{
                        'type': 'checkbox',
                        'name': 'Co-ordinates tooltip',
                        'valueKey': 'canShowTooltip'
                    },{
                        'type': 'checkbox',
                        'name': 'Drawable line of best fit',
                        'valueKey': 'canDrawLine'
                    },{
                        'type': 'checkbox',
                        'name': 'Auto-generate line of best fit',
                        'valueKey': 'canShowAutoLine'
                    }]
                },
            ];
        };

        $scope.initPreferences();
        $injector.invoke(this.init, this, {
            $scope: $scope
        });

        /**
         * Sets the response data for filling the chart settings.
         * @param {Object} attachment - The attachment containing the response data.
         */
        this.setResponseFill = function(attachment) {
            if (typeof(attachment.series) != 'undefined' && attachment.series.length >= 1) {
                angular.copy(attachment.series, $scope.series);
            }

            if (typeof(attachment.canShowTooltip) != 'undefined') {
                angular.copy(attachment.canShowTooltip, $scope.canShowTooltip);
            }

            if (typeof(attachment.chartSettings) != 'undefined' && $scope.chartSettings != attachment.chartSettings) {
                angular.copy(attachment.chartSettings, $scope.chartSettings);

                $timeout(function() {
                    $scope.plotGraph();
                    $scope.showCharts = true;
                    $scope.showWizard = true;
                }, 1000);
            }
        };

        /**
         * Sets the response data for collecting the chart settings.
         * @param {Object} attachment - The attachment to collect the response data.
         * @returns {boolean} - Whether the response data is valid.
         */
        this.setResponseCollect = function(attachment) {
            if ($scope.series.length >= 1) {
                attachment.series = angular.copy($scope.series);
            }

            console.log('_gbc_save', 'chartSettings', $scope.chartSettings);

            attachment.chartSettings = angular.copy($scope.chartSettings);

            if (
                (angular.isDefined(attachment.series) && attachment.series.length > 0) ||
                (angular.isArray(attachment.chartSettings.x)) ||
                (angular.isArray(attachment.chartSettings.y)) ||
                (angular.isDefined(attachment.chartSettings.title) && attachment.chartSettings.title.length > 0) ||
                (angular.isDefined(attachment.chartSettings.canShowTooltip)) ||
                (angular.isDefined(attachment.chartSettings.canDrawLine)) ||
                (angular.isDefined(attachment.chartSettings.canShowAutoLine)) ||
                (angular.isDefined(attachment.chartSettings.scatterLine) && attachment.chartSettings.scatterLine.length > 0) ||
                (angular.isDefined(attachment.chartSettings.xLabel) && attachment.chartSettings.xLabel.length > 0) ||
                (angular.isDefined(attachment.chartSettings.yLabel) && attachment.chartSettings.yLabel.length > 0) ||
                (angular.isDefined(attachment.chartSettings.selectedChart) && attachment.chartSettings.selectedChart != 'pie') ||
                (angular.isDefined(attachment.chartSettings.pie.labels) && angular.isArray(attachment.chartSettings.pie.labels)) ||
                (angular.isDefined(attachment.chartSettings.pie.values) && angular.isArray(attachment.chartSettings.pie.values))
            ) {
                console.log('_gbc_save yes', true, attachment);
                return true;
            }

            console.log('_gbc_save noes', false, attachment);
            return false;
        };
    }
}

export { AutoPlotFromTableController };

/**
 * Registers the AsfAutoPlotFromTableObject directive.
 * @param {Object} module - The Angular module to register the directive with.
 */
export default function registerAsfAutoPlotFromTableObjectDirective(module) {
    module.directive('asfAutoPlotFromTableObject', function() {
        return {
            restrict: 'EA',
            replace: true,
            scope: {
                "contentObject": "="
            },
            templateUrl: "/views/ib/contentobjects/autoPlotFromTable.html",
            controller: AutoPlotFromTableController,
            link: function(scope, element, attrs) {
                scope.table = new Handsontable(element.find('#tableView')[0], scope.tableConfig);
            }
        };
    });
    registerContentObject(module);
}

/**
 * Registers the content object in the ContentObjectRegistryService.
 * @param {Object} module - The Angular module to register the content object with.
 */
export function registerContentObject(module) {
    module.run(['ContentObjectRegistryService', function(ContentObjectRegistryService) {
        var obj = [{
            "uuid": "",
            "content": "",
            "objectType": "auto-plot-from-table",
            "type": "contentObject",
            "contentReference": false,
            "preferences": {
                "showSubtitleOnPlay": false,
                "includedInPDF": true,
                "canShowTooltip": false,
                "canDrawLine": false,
                "canShowAutoLine": false
            }
        }];
        var objProperties = {
            type: "auto-plot-from-table",
            label: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.AUTO-PLOT.LABEL',
            cssClass: "new-audio-object",
            icon: "cms/icon-auto-plot.png",
            description: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.AUTO-PLOT.DESCRIPTION',
            group: {
                id: 3
            }
        };
        ContentObjectRegistryService.register(obj, objProperties);
    }]);
}